/**
 * The base field of view per camera view, for every mode at once.
 *
 * Two numbers for the tab, one per `CameraModeId`, written by the settings knobs and read per tick
 * by `viewPreference()` on its way into a mode's step. Nothing downstream reads this module:
 * a camera view stays a pure function of the `ViewPreference` it was handed, on the terms
 * [viewPreference](../../sim/camera/viewPreference.ts) states, which is also where the bounds and
 * the default live: a slider that owned its own range could offer a value the camera clamps away.
 *
 * Per camera view rather than one number, because the two lenses are two different instruments: a
 * first-person figure is how much of the world sits around the sights, a third-person one is how
 * much of the world sits around the body, and a player who widens one has said nothing about the
 * other.
 */

import { clamp } from '@/sim/math';
import { DEFAULT_FOV_DEGREES, MAX_FOV_DEGREES, MIN_FOV_DEGREES } from '@/sim/camera/viewPreference';
import type { CameraModeId } from '@/sim/camera/cameraModeContract';

const degrees: Record<CameraModeId, number> = {
  firstPerson: DEFAULT_FOV_DEGREES,
  thirdPerson: DEFAULT_FOV_DEGREES,
};

export function baseFovDegrees(view: CameraModeId): number {
  return degrees[view];
}

/** Clamped here as well as in the settings layer: this is the lens the camera actually takes. */
export function setBaseFovDegrees(view: CameraModeId, value: number): void {
  degrees[view] = clamp(value, MIN_FOV_DEGREES, MAX_FOV_DEGREES);
}
